import React, { CSSProperties } from 'react';

import './GridGroupEvent.scss';
import { GroupEventElement } from './GridBody';
import { BubbleBadge } from './BubbleUI';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getBasicTimeString } from '@go-shared/lib/event';
import { VenueRequestStatus } from '@go-shared/types/groupBooking';

export interface CSSPropertiesWithVars extends CSSProperties {
  '--buffer-start'?: string;
  '--buffer-end'?: string;
  '--event-duration-end'?: string;
  '--backgroundColor'?: string;
}

const BACKGROUND_COLOR_APPROVED = '#547c5e';
const BACKGROUND_COLOR_MODIFIED = '#f5b700';
const BACKGROUND_COLOR_PENDING = '#ababab';

// Diagonal stripes SVG alternating between two colors (`r, g, b` strings)
function stripesBackgroundImage(stripeColor: string, altStripeColor: string) {
  const fill = (rgb: string) => `rgba(${rgb.replace(/, /g, '%2C%20')}%2C%201)`;
  return (
    `url("data:image/svg+xml,` +
    `%3Csvg%20id%3D'stripes-svg'%20xmlns%3D'http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg'%20width%3D'100%25'%20height%3D'100%25'%3E%3Cdefs%3E%3Cpattern%20id%3D'svg-stripes-pattern-7515'%20patternTransform%3D'rotate(45)'%20width%3D'100%25'%20height%3D'4'%20x%3D'0'%20y%3D'0'%20patternUnits%3D'userSpaceOnUse'%3E%3Cg%3E` +
    `%3Crect%20x%3D'0'%20y%3D'0'%20width%3D'100%25'%20height%3D'2'%20fill%3D'${fill(stripeColor)}'%3E%3C%2Frect%3E` +
    `%3Crect%20x%3D'0'%20y%3D'2'%20width%3D'100%25'%20height%3D'2'%20fill%3D'${fill(altStripeColor)}'%3E%3C%2Frect%3E` +
    `%3C%2Fg%3E%3C%2Fpattern%3E%3C%2Fdefs%3E%3Crect%20x%3D'0'%20y%3D'0'%20width%3D'100%25'%20height%3D'100%25'%20fill%3D'url(%23svg-stripes-pattern-7515)'%3E%3C%2Frect%3E%3C%2Fsvg%3E%0A")`
  );
}

function getStatusBackground(status: VenueRequestStatus) {
  switch (status) {
    case 'approved':
      return {
        backgroundColor: BACKGROUND_COLOR_APPROVED,
        backgroundImage: stripesBackgroundImage('62, 102, 70', '52, 86, 59'),
      };
    case 'pending':
      return {
        backgroundColor: BACKGROUND_COLOR_PENDING,
        backgroundImage: stripesBackgroundImage('161, 161, 161', '151, 151, 151'),
      };
    default:
      return {
        backgroundColor: BACKGROUND_COLOR_MODIFIED,
        backgroundImage: stripesBackgroundImage('235, 173, 0', '225, 163, 0'),
      };
  }
}

function EVENT_BORDERS(color: string, buffer: boolean = false) {
  return buffer
    ? {
        borderRight: `solid thin ${color}`,
      }
    : {
        borderBottom: `solid thin ${color}`,
        borderRight: `solid thin ${color}`,
      };
}

function GridGroupEvent(props: GroupEventElement) {
  const { backgroundImage, backgroundColor } = getStatusBackground(props.groupEvent.status);

  const hasBreakdownTime: boolean = !!props.breakdownTimeEnd && props.breakdownTimeEnd > 0;

  return (
    <div
      className="GridGroupEvent absolute select-none shadow bg-forest"
      style={
        {
          ...EVENT_BORDERS('white', hasBreakdownTime),
          backgroundImage,
          ...props.style,
          '--buffer-start': `${props.setUpTimeStart}px`,
          '--buffer-end': `${props.breakdownTimeEnd}px`,
          '--backgroundColor': backgroundColor,
        } as CSSPropertiesWithVars
      }
    >
      <div className="w-full h-full overflow-hidden text-white" style={{ padding: '4px 4px' }}>
        <div className="text-white font-bold" style={{ fontSize: 10, lineHeight: '10px' }}>
          {getBasicTimeString(props.groupEvent, props.meta.hourFormat)}
        </div>
        <div className="text-white text-xs leading-4">
          {props.groupEvent.group} - {props.groupEvent.title}
        </div>
        <BubbleBadge variant="slate" className="GridGroupEvent-status mt-1">
          {props.groupEvent.status}
        </BubbleBadge>
        <div
          className="absolute bottom-0 right-0 whitespace-nowrap flex justify-center 
            not-italic max-w-full overflow-hidden pointer-events-none border-t border-l border-white"
          style={{ height: 15 }}
        >
          <BubbleBadge
            variant="slate"
            style={{ width: 15, height: 15 }}
            className="p-0-i grid items-center justify-center text-center rounded-0-i"
          >
            <FontAwesomeIcon icon="people-group" />
          </BubbleBadge>
        </div>
      </div>
    </div>
  );
}
export default GridGroupEvent;
